import React from "react";
import { formatRecordLabelTemplate } from "../../recordLabelTemplate";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton from "@mui/joy/ListItemButton";
import ListItemContent from "@mui/joy/ListItemContent";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import Button from "@mui/joy/Button";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import * as MuiIcons from "@mui/icons-material";
import StatusChip from "./StatusChip";
import {
  sanitizeStatusColors,
  type StatusColor,
} from "../canvasStyleOverrides";

/**
 * Opt-in "icon row list" display for a canvas VIEW_GRID: `display: "list"` with a `list_config`
 * renders each record as a row (leading icon, primary text, optional secondary text, trailing status
 * pill or chevron) instead of a data grid. Row clicks use the grid's existing row handler.
 */
export interface RowListConfig {
  /** Fixed MUI icon name for every row. */
  icon?: string;
  /** Field whose value names the MUI icon for that row; wins over `icon`. */
  iconField?: string;
  primaryField: string;
  recordLabelTemplate?: string;
  secondaryField?: string;
  /** Field rendered as a status pill at the end of the row. */
  statusField?: string;
  statusColors?: Record<string, StatusColor>;
  /** Show a chevron at the end of the row. */
  chevron?: boolean;
}

const text = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);

function pick(source: Record<string, unknown>, camel: string, snake: string) {
  const value = source[camel] ?? source[snake];
  return typeof value === "string" && value ? value : undefined;
}

/** Read `display` and `list_config` from an element config; undefined unless a list is configured. */
export function readRowListConfig(
  config: Record<string, unknown>,
): RowListConfig | undefined {
  if (config.display !== "list") return undefined;
  const raw = config.list_config ?? config.listConfig;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const source = raw as Record<string, unknown>;
  const primaryField = pick(source, "primaryField", "primary_field");
  if (!primaryField) return undefined;
  return {
    icon: pick(source, "icon", "icon"),
    iconField: pick(source, "iconField", "icon_field"),
    primaryField,
    ...(pick(source, "recordLabelTemplate", "record_label_template")
      ? {
          recordLabelTemplate: pick(
            source,
            "recordLabelTemplate",
            "record_label_template",
          ),
        }
      : {}),
    secondaryField: pick(source, "secondaryField", "secondary_field"),
    statusField: pick(source, "statusField", "status_field"),
    statusColors: sanitizeStatusColors(
      source.statusColors ?? source.status_colors,
    ),
    chevron: source.chevron === true,
  };
}

function iconFor(name: string): React.ElementType | null {
  const icons = MuiIcons as Record<string, React.ElementType | undefined>;
  return icons[name] || icons[`${name}Outlined`] || null;
}

interface Props {
  rows: Array<Record<string, unknown>>;
  config: RowListConfig;
  title?: string;
  onRowSelect: (rowId: string, row: Record<string, unknown>) => void;
  /** Zero-based page, page size and total record count; a pager shows when there is more than one page. */
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
}

export default function CanvasRowList({
  rows,
  config,
  title,
  onRowSelect,
  page = 0,
  pageSize,
  totalCount,
  onPageChange,
}: Props) {
  const pageCount =
    pageSize && totalCount ? Math.ceil(totalCount / pageSize) : 1;
  const Chevron = iconFor("ChevronRight");
  return (
    <>
      <List aria-label={title || "Records"} data-testid="canvas-row-list">
        {rows.map((row, index) => {
          const rowId = text(row._rowId ?? row.id ?? index);
          const iconName = config.iconField
            ? text(row[config.iconField]) || config.icon
            : config.icon;
          const Icon = iconName ? iconFor(iconName) : null;
          const secondary = config.secondaryField
            ? text(row[config.secondaryField])
            : "";
          const status = config.statusField
            ? text(row[config.statusField])
            : "";
          return (
            <ListItem key={rowId}>
              <ListItemButton onClick={() => onRowSelect(rowId, row)}>
                {Icon && (
                  <ListItemDecorator>
                    <Icon fontSize="small" />
                  </ListItemDecorator>
                )}
                <ListItemContent>
                  <Typography level="title-sm">
                    {config.recordLabelTemplate
                      ? formatRecordLabelTemplate(
                          config.recordLabelTemplate,
                          row,
                        )
                      : text(row[config.primaryField])}
                  </Typography>
                  {secondary && (
                    <Typography level="body-xs">{secondary}</Typography>
                  )}
                </ListItemContent>
                {status && (
                  <StatusChip
                    value={status}
                    statusColors={config.statusColors}
                  />
                )}
                {config.chevron && Chevron && (
                  <Chevron fontSize="small" data-testid="row-chevron" />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      {onPageChange && pageCount > 1 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 1 }}>
          <Button
            size="sm"
            variant="outlined"
            disabled={page <= 0}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </Button>
          <Typography level="body-sm">
            Page {page + 1} of {pageCount}
          </Typography>
          <Button
            size="sm"
            variant="outlined"
            disabled={page >= pageCount - 1}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </Stack>
      )}
    </>
  );
}
